import { convertInvoiceV3toInvoiceModel, InvoiceModel, InvoiceModelV3, isInvoiceModelV3 } from '@frontend/api-invoices';
import { calculatePaidInvoiceTotal, calculateTotalNet, calculateTotalPaidWithoutRefunds } from './amount';
import { isPaidInvoice } from './status';

/**
 * Calculate Surcharge amount base calculation.
 * - Applied surcharge amount / calculated surcharge based on basis points.
 *
 * NOTE:
 * - Internal use only. Do not export.
 * - For general purposes, use calculateAppliedOrEstimatedSurcharge, which has an isPaid wrapper around this function.
 */
export const calculateBasicSurchargeAmount = (invoice: InvoiceModel | InvoiceModelV3) => {
  const convertedInvoice = isInvoiceModelV3(invoice) ? convertInvoiceV3toInvoiceModel(invoice) : invoice;

  if (convertedInvoice.appliedSurchargeAmount) {
    return convertedInvoice.appliedSurchargeAmount;
  }
  if (convertedInvoice.configuredSurchargeBasisPoints) {
    return convertedInvoice.billedAmount * (convertedInvoice.configuredSurchargeBasisPoints / 10000);
  }
  return 0;
};

/**
 * Calculate Surcharge amount + isPaid Check
 * - for paid invoices, forces applied surcharge amount
 * - else wraps calculateBasicSurchargeAmount
 */
export const calculateAppliedOrEstimatedSurcharge = (invoice: InvoiceModel | InvoiceModelV3) => {
  const convertedInvoice = isInvoiceModelV3(invoice) ? convertInvoiceV3toInvoiceModel(invoice) : invoice;

  if (convertedInvoice.surchargingEnabled) {
    if (isPaidInvoice(convertedInvoice)) {
      return convertedInvoice.appliedSurchargeAmount ?? 0;
    } else {
      return calculateBasicSurchargeAmount(convertedInvoice);
    }
  }

  return 0;
};

/**
 * Calculate invoice total or paid total with surcharge
 * Safe to use external. Has isPaid wrapper.
 */
export const calculatePaidOrUnpaidInvoiceTotalWithSurcharge = (invoice: InvoiceModel | InvoiceModelV3) => {
  const convertedInvoice = isInvoiceModelV3(invoice) ? convertInvoiceV3toInvoiceModel(invoice) : invoice;

  return isPaidInvoice(convertedInvoice)
    ? calculatePaidInvoiceTotalWithSurcharge(convertedInvoice)
    : calculateUnpaidInvoiceTotalWithSurcharge(convertedInvoice);
};

/**
 * Try not to use externally.
 * Instead, use calculatePaidOrUnpaidInvoiceTotalWithSurcharge, which has an isPaid wrapper around this function.
 */
export const calculateUnpaidInvoiceTotalWithSurcharge = (invoice: InvoiceModel | InvoiceModelV3) => {
  const convertedInvoice = isInvoiceModelV3(invoice) ? convertInvoiceV3toInvoiceModel(invoice) : invoice;
  const surchargeAmount = invoice.surchargingEnabled ? calculateBasicSurchargeAmount(convertedInvoice) : 0;
  return convertedInvoice.billedAmount + surchargeAmount;
};

/**
 * Try not to use externally.
 * Instead, use calculatePaidOrUnpaidInvoiceTotalWithSurcharge, which has an isPaid wrapper around this function.
 */
export const calculatePaidInvoiceTotalWithSurcharge = (invoice: InvoiceModel | InvoiceModelV3) => {
  const convertedInvoice = isInvoiceModelV3(invoice) ? convertInvoiceV3toInvoiceModel(invoice) : invoice;

  const totalPaymentsMinusRefunds = calculatePaidInvoiceTotal(convertedInvoice);

  const surchargeAmount =
    isPaidInvoice(convertedInvoice) && convertedInvoice.surchargingEnabled
      ? convertedInvoice.appliedSurchargeAmount ?? 0
      : 0;

  return totalPaymentsMinusRefunds + surchargeAmount;
};

/**
 * Calculate remaining balance with surcharge
 * - for unpaid, returns total + surcharge
 * - for paid, returns (billed amount + surcharge) - paid amount + surcharge
 */
export const calculateRemainingBalanceWithSurcharge = (invoice: InvoiceModel | InvoiceModelV3) => {
  const convertedInvoice = isInvoiceModelV3(invoice) ? convertInvoiceV3toInvoiceModel(invoice) : invoice;

  const totalDue = calculateUnpaidInvoiceTotalWithSurcharge(convertedInvoice);

  if (!isPaidInvoice(convertedInvoice)) {
    // just return the total ... they haven't paid anything
    return totalDue;
  }

  const totalRemaining = totalDue - calculatePaidInvoiceTotalWithSurcharge(convertedInvoice);

  return Math.max(0, totalRemaining);
};

export const calculateRemainingBalanceWithoutSurcharge = (invoice: InvoiceModel | InvoiceModelV3) => {
  const convertedInvoice = isInvoiceModelV3(invoice) ? convertInvoiceV3toInvoiceModel(invoice) : invoice;

  const totalDue = convertedInvoice.billedAmount;

  if (!isPaidInvoice(convertedInvoice)) {
    // just return the total ... they haven't paid anything
    return totalDue;
  }

  const totalRemaining = totalDue - calculatePaidInvoiceTotal(convertedInvoice);

  return Math.max(0, totalRemaining);
};

/**
 * Calculate total net with surcharge
 */
export const calculateTotalNetWithSurcharge = (invoice: InvoiceModel | InvoiceModelV3) => {
  const convertedInvoice = isInvoiceModelV3(invoice) ? convertInvoiceV3toInvoiceModel(invoice) : invoice;

  const totalNet = calculateTotalNet(invoice);

  return totalNet + calculateAppliedOrEstimatedSurcharge(convertedInvoice);
};

export const calculateTotalPaidWithoutRefundsWithSurcharge = (invoice: InvoiceModel) => {
  const totalPaid = calculateTotalPaidWithoutRefunds(invoice);

  const surchargeAmount = invoice.surchargingEnabled ? invoice.appliedSurchargeAmount ?? 0 : 0;

  return totalPaid + surchargeAmount;
};
